import {useState, useEffect} from 'react';
import { ColorExtractor } from 'react-color-extractor'
import { isCoreContract } from '../../lib/utils';

const CacheImage = ({imageID, contractAddress, getColors, projectId}) => {
    const handleImageLoad = () => {
      // Add the image to the browser's cache
      new Image().src = src;
    };
    
    // Determine if this is a Core contract
    const isCore = isCoreContract(contractAddress);
    
    // For Core contracts, use standard format
    // For PBAB/Engine, use media-proxy.artblocks.io
    const getImageUrl = () => {
      if (isCore || !contractAddress) {
        return `https://media.artblocks.io/thumb/${imageID}.png`;
      } else {
        // PBAB/Engine: Use media-proxy.artblocks.io (not media.artblocks.io)
        return `https://media-proxy.artblocks.io/${contractAddress.toLowerCase()}/${imageID}.png`;
      }
    };
    
    const [src, setSrc] = useState(getImageUrl());
    const [errorCount, setErrorCount] = useState(0);
    const [triedTokenApi, setTriedTokenApi] = useState(false);
    
    useEffect(() => {
        setSrc(getImageUrl());
        setErrorCount(0);
        setTriedTokenApi(false);
    }, [imageID, contractAddress]);
    
    // Fetch image URL from Art Blocks token API for PBAB/Engine contracts
    const fetchTokenImageUrl = async (contractAddr, tokenId) => {
      try {
        const response = await fetch(`https://token.artblocks.io/${contractAddr}/${tokenId}`);
        if (response.ok) {
          const data = await response.json();
          // The API returns metadata with an 'image' field
          if (data.image) {
            return data.image;
          }
        }
      } catch (error) {
        console.warn(`[CacheImage] Failed to fetch from token API:`, error);
      }
      return null;
    };
    
    const handleImageError = async (e) => {
        const newErrorCount = errorCount + 1;
        setErrorCount(newErrorCount);
        
        // Debug logging (only log first few errors to avoid spam)
        if (newErrorCount <= 3) {
          console.warn(`[CacheImage] Image failed to load (attempt ${newErrorCount}):`, {
            imageID,
            projectId,
            contractAddress,
            isCore,
            currentUrl: src,
            calculatedImageID: projectId ? `${projectId} * 1000000 = ${projectId * 1000000}` : 'N/A',
            error: e
          });
        }
        
        // For Core contracts, only try standard fallbacks
        if (isCore || !contractAddress) {
          if (newErrorCount === 1) {
            // Try without /thumb/ prefix
            const newUrl = `https://media.artblocks.io/${imageID}.png`;
            console.log(`[CacheImage] Core contract fallback 1:`, newUrl);
            setSrc(newUrl);
          }
          return;
        }
        
        // For PBAB/Engine contracts, try multiple strategies
        if (newErrorCount === 1) {
          // Fallback 1: Try media.artblocks.io format (in case some use it)
          const newUrl = `https://media.artblocks.io/${contractAddress.toLowerCase()}/${imageID}.png`;
          console.log(`[CacheImage] PBAB/Engine fallback 1:`, newUrl);
          setSrc(newUrl);
        } else if (newErrorCount === 2) {
          // Fallback 2: Try standard format without contract
          const newUrl = `https://media.artblocks.io/${imageID}.png`;
          console.log(`[CacheImage] PBAB/Engine fallback 2:`, newUrl);
          setSrc(newUrl);
        } else if (newErrorCount === 3) {
          // Fallback 3: Try standard thumb format
          const newUrl = `https://media.artblocks.io/thumb/${imageID}.png`;
          console.log(`[CacheImage] PBAB/Engine fallback 3:`, newUrl);
          setSrc(newUrl);
        } else if (newErrorCount === 4 && !triedTokenApi) {
          // Fallback 4: Try fetching from Art Blocks token API
          setTriedTokenApi(true);
          console.log(`[CacheImage] PBAB/Engine fallback 4: Fetching from token API...`);
          const imageUrl = await fetchTokenImageUrl(contractAddress.toLowerCase(), imageID.toString());
          if (imageUrl) {
            console.log(`[CacheImage] Found image URL from token API:`, imageUrl);
            setSrc(imageUrl);
          } else {
            console.warn(`[CacheImage] Token API did not return image URL for contract ${contractAddress}, tokenId ${imageID}`);
          }
        }
        // If all fail, the image will remain broken (PBAB/Engine images may not be available)
    }
  
    return (
        <ColorExtractor getColors={getColors}>
            <img 
              className="nft-image" 
              src={src} 
              onLoad={handleImageLoad} 
              onError={handleImageError} 
              alt={`ArtBlock ${imageID} thumbnail`}
              loading="lazy"
            />
        </ColorExtractor>
    );
}

export default CacheImage